import {
    Box,
    Button,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Block, Delete, PhoneDisabled } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import {
    cadastrarTelefoneBloqueado,
    excluirTelefoneBloqueado,
} from "../../../services/telefonesBloqueados"
import { TelefoneBloqueadosActionsTypes } from "../context/telefonesBloqueadosActions"
import { useTelefoneBloqueadosContext } from "../context/useTelefoneBloqueadosContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.4)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    emptyText: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(3),
    },
}))

const formularioVazio = { pais: "55", telefone: "", observacao: "" }

export function TelefoneBloqueadosForm() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento } = useTelefoneBloqueadosContext()
    const { enqueueSnackbar } = useSnackbar()
    const [formulario, setFormulario] = useState(formularioVazio)
    const [salvando, setSalvando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleChange =
        (campo: keyof typeof formularioVazio) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setFormulario(prev => ({ ...prev, [campo]: value }))
        }

    const handleCadastrar = async () => {
        if (!formulario.telefone.trim()) {
            alertStart("O campo Telefone é obrigatório.", "error")
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setSalvando(true)
        try {
            const response = await cadastrarTelefoneBloqueado(autorizacao, formulario)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao cadastrar telefone", "error")
            } else {
                dispatch({ type: TelefoneBloqueadosActionsTypes.CARREGAR, payload: response })
                setFormulario(formularioVazio)
                alertStart("Telefone bloqueado cadastrado com sucesso!", "success")
            }
        } finally {
            setSalvando(false)
        }
    }

    const handleExcluir = async (id: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        const response = await excluirTelefoneBloqueado(autorizacao, id)
        if (response && "retornoErro" in response) {
            alertStart(response.mensagem || "Erro ao excluir telefone", "error")
            return
        }
        dispatch({
            type: TelefoneBloqueadosActionsTypes.CARREGAR,
            payload: dados.lista.filter(t => t.id !== id),
        })
        alertStart("Telefone removido com sucesso!", "success")
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Telefones Bloqueados
                </Typography>

                {/* Formulário */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <PhoneDisabled className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Cadastrar novo telefone bloqueado
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={4} sm={2}>
                                <TextField
                                    label="País"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.pais}
                                    onChange={handleChange("pais")}
                                />
                            </Grid>
                            <Grid item xs={8} sm={4}>
                                <TextField
                                    label="Telefone"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.telefone}
                                    onChange={handleChange("telefone")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Observação"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.observacao}
                                    onChange={handleChange("observacao")}
                                />
                            </Grid>
                            <Grid item xs={12} style={{ textAlign: "center" }}>
                                <Button
                                    className={classes.saveButton}
                                    variant="contained"
                                    onClick={handleCadastrar}
                                    disabled={salvando}
                                >
                                    {salvando ? "Salvando..." : "Salvar"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Lista */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Block className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Telefones cadastrados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.lista.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                Nenhum telefone bloqueado cadastrado.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table size="small" style={{ width: "100%", minWidth: 480 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 80 }}>País</TableCell>
                                            <TableCell style={{ minWidth: 140 }}>Telefone</TableCell>
                                            <TableCell style={{ minWidth: 200 }}>
                                                Observação
                                            </TableCell>
                                            <TableCell style={{ minWidth: 60 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.lista.map(telefone => (
                                            <TableRow key={telefone.id}>
                                                <TableCell>{telefone.pais}</TableCell>
                                                <TableCell className={classes.nomeCell}>
                                                    {telefone.telefone}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={telefone.observacao}
                                                >
                                                    {telefone.observacao}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExcluir(telefone.id)}
                                                        title="Excluir"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
